import "core-js/modules/es6.function.name.js";
/**
 * Default value for the mainlist.
 *
 * @type {{data: [], meta: {path: string, total: number, perPage: number, lastPage: number, from: number, to: number, currentPage: number}, selected: []}}
 */
export var mainlistDefault = {
  selected: [],
  data: [],
  meta: {
    currentPage: 1,
    from: 0,
    lastPage: 1,
    path: '',
    perPage: 100,
    to: 0,
    total: 0
  },
  expanded: [],
  expandedChildren: [],
  searchTriggered: null
};

/*
 * No data message for empty panels
 */
export var NO_DATA_MESSAGE = 'No data.';

/**
 * Defines panel side names.
 *
 * @type {string}
 */
export var LEFT = 'left';
export var RIGHT = 'right';
export var BOTH = 'both';
export var PANEL_SIDES = ['leftA', 'leftB', 'rightA', 'rightB'];

/**
 * Defines minimum number of letters to be able to perform a search.
 *
 * @type {number}
 */
export var MIN_SEARCH_LETTERS = 2;

/**
 * Infinity scroll offset for triggering the API.
 *
 * @type {number}
 */
export var INFINITY_SCROLL_OFFSET = 1500;

/**
 * Maximum allowed items to be selected in right panel.
 * @type {number}
 */
export var MAX_SELECTED_ITEMS = 2000;

/**
 * Number of seconds after the app is consider as idle.
 * @type {number}
 */
export var IDLE_TIME = process.env.IDLE_TIME || 45; // 45 seconds

/**
 * Number of second for interval in which unseen request should be sent.
 * @type {number}
 */
export var UNSEEN_INTERVAL = process.env.UNSEEN_INTERVAL || 30; // seconds

/**
 * Number of minutes before the Inactive User dialog appears.
 * @type {number}
 */
export var INACTIVE_PERIOD = process.env.INACTIVE_PERIOD || 20; // 20 minutes

/**
 * Number of seconds shown as countdown in the Inactive User Dialog.
 * @type {string|number}
 */
export var LOGOUT_DIALOG_COUNTDOWN = process.env.LOGOUT_DIALOG_COUNTDOWN || 120; // 120 seconds

export var MFA_DISABLED = process.env.MFA_DISABLED || false;

/**
 * A list of all entities involved in the Audit system.
 *
 * - model | ClassName on backend
 * - slug | Web-safe name
 * - label | Display name
 * - resource | String in the API url related to the model
 * - state | State name used in conceptEntities store
 *
 * @type []
 */
export var auditEntities = [{
  model: 'App\\Cui',
  slug: 'cui',
  label: 'CUI',
  resource: 'cuis',
  state: 'cui'
}, {
  model: 'App\\CuiNGram',
  slug: 'cui_n_gram',
  label: 'CUI N-gram',
  hideForAdmins: true
}, {
  model: 'App\\Query',
  slug: 'query',
  label: 'Query',
  hideForAdmins: true
}, {
  model: 'App\\MedlinePlus',
  slug: 'medline_plus',
  label: 'Medline Plus'
}, {
  model: 'App\\CuiMedlinePlusNGram',
  slug: 'medline_plus_cui_n_gram',
  label: 'Medline Plus CUI N-gram'
}, {
  model: 'App\\Wiki',
  slug: 'wiki',
  label: 'Wikipedia'
}, {
  model: 'App\\CuiWikiNGram',
  slug: 'wiki_cui_n_gram',
  label: 'Wikipedia CUI N-gram'
}, {
  model: 'App\\Mesh',
  slug: 'mesh',
  label: 'MeSH',
  resource: 'meshes',
  state: 'mesh'
}, {
  model: 'App\\Wui',
  slug: 'wui',
  label: 'WUI',
  resource: 'wuis',
  state: 'wui',
  hideForAdmins: true
}, {
  model: 'App\\WuiNGram',
  slug: 'wui_n_gram',
  label: 'WUI N-gram',
  hideForAdmins: true
}, {
  model: 'App\\Reddit',
  slug: 'reddit',
  label: 'Reddit',
  resource: 'reddits',
  state: 'reddit',
  hideForAdmins: true
}, {
  model: 'App\\RedditCuiNGram',
  slug: 'reddit_cui_n_gram',
  label: 'Reddit CUI N-gram',
  hideForAdmins: true
}, {
  model: 'App\\RedditWuiNGram',
  slug: 'reddit_wui_n_gram',
  label: 'Reddit WUI N-gram',
  hideForAdmins: true
}];

/**
 * Here we define a list of available action entities and their
 * modal title, entity to include, entity item name and
 * should related data be deleted.
 *
 * @type {function(string)}
 */
export var actionProperties = function actionProperties(actionName) {
  switch (actionName) {
    case 'mesh-link':
      return {
        title: 'List of CUIs',
        include: 'cui',
        getName: function getName(entity) {
          return entity.cui.name;
        },
        deleteRelated: 1,
        actionCommentRequired: false
      };
    case 'cui-confirm':
    case 'cui-detach':
      return {
        title: 'List of Queries',
        include: 'theQuery',
        getName: function getName(entity) {
          return entity.the_query.name;
        },
        deleteRelated: 0,
        actionCommentRequired: false
      };
    case 'wui-confirm':
    case 'wui-detach':
      return {
        title: 'List of Queries',
        include: 'theQuery',
        getName: function getName(entity) {
          return entity.the_query.name;
        },
        deleteRelated: 0,
        actionCommentRequired: false
      };
    case 'reddit-cui-confirm':
    case 'reddit-cui-detach':
      return {
        title: 'Reddit sentences',
        include: 'reddit',
        getName: function getName(entity) {
          return entity.reddit.name;
        },
        deleteRelated: 0,
        actionCommentRequired: false
      };
    case 'reddit-wui-confirm':
    case 'reddit-wui-detach':
      return {
        title: 'Reddit sentences',
        include: 'reddit',
        getName: function getName(entity) {
          return entity.reddit.name;
        },
        deleteRelated: 0,
        actionCommentRequired: false
      };
    case 'medline-plus-cui-confirm':
    case 'medline-plus-cui-detach':
      return {
        title: 'List of Medline Pluses',
        include: 'medlinePlus.articleTitle',
        getName: function getName(entity) {
          return entity.medline_plus.name;
        },
        deleteRelated: 0,
        actionCommentRequired: false,
        model: 'medline_plus'
      };
    case 'wiki-cui-confirm':
    case 'wiki-cui-detach':
      return {
        title: 'List of Wikis',
        include: 'wiki.articleTitle',
        getName: function getName(entity) {
          return entity.wiki.name;
        },
        deleteRelated: 0,
        actionCommentRequired: false,
        model: 'wiki'
      };
    case 'query-cui-add-tag':
      return {
        title: 'Query CUI tags',
        include: 'sentenceCuiQuery',
        getName: function getName(entity) {
          return entity.sentence_cui_query.name;
        },
        deleteRelated: 1,
        actionCommentRequired: false
      };
    case 'query-wui-add-tag':
      return {
        title: 'Query WUI tags',
        include: 'sentenceWuiQuery',
        getName: function getName(entity) {
          return entity.sentence_wui_query.name;
        },
        deleteRelated: 1,
        actionCommentRequired: false
      };
    case 'reddit-cui-add-tag':
      return {
        title: 'Reddit CUI tags',
        include: 'sentenceCuiReddit',
        getName: function getName(entity) {
          return entity.sentence_cui_reddit.name;
        },
        deleteRelated: 1,
        actionCommentRequired: false
      };
    case 'reddit-wui-add-tag':
      return {
        title: 'Reddit WUI tags',
        include: 'sentenceWuiReddit',
        getName: function getName(entity) {
          return entity.sentence_wui_reddit.name;
        },
        deleteRelated: 1,
        actionCommentRequired: false
      };
    case 'medline-plus-cui-add-tag':
      return {
        title: 'MedlinePlus CUI tags',
        include: 'sentenceCuiMedlinePlus',
        getName: function getName(entity) {
          return entity.sentence_cui_medline_plus.name;
        },
        deleteRelated: 1,
        actionCommentRequired: false
      };
    case 'wiki-cui-add-tag':
      return {
        title: 'Wikipedia CUI tags',
        include: 'sentenceCuiWiki',
        getName: function getName(entity) {
          return entity.sentence_cui_wiki.name;
        },
        deleteRelated: 1,
        actionCommentRequired: false
      };
  }
};
export var itemsPerPage = [10, 20, 50, 100];
export var filterOptions = [{
  label: 'AND',
  value: 'and'
}, {
  label: 'OR',
  value: 'or'
}, {
  label: 'NOT',
  value: 'not'
}, {
  label: 'Literal',
  value: 'literal'
}, {
  label: 'Contiguous',
  value: 'contiguous'
}, {
  label: 'Discontiguous',
  value: 'discontiguous'
}];
export var matchOptions = [{
  label: 'regular',
  value: 'regular'
}, {
  label: 'starts with',
  value: 'starts_with'
}, {
  label: 'whole word',
  value: 'whole_word'
}];
export var MEDLINE_PLUS_FIELDS_TO_HIDE = ['id', 'medline_plus_id', 'medline_plus_section_id', 'model', 'model_id'];
export var WIKI_FIELDS_TO_HIDE = ['id', 'wiki_id', 'wiki_section_id', 'model', 'model_id'];
export var wikiCategoryFilterOptions = [{
  label: 'regular',
  value: 'regular'
}, {
  label: 'literal',
  value: 'literal'
}, {
  label: 'whole word',
  value: 'whole_word'
}];